/**
 * Authentication Context Provider
 * Manages authentication state and provides auth methods to the app
 */

import React, { createContext, useContext, useEffect, useState } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase, getVenueIdFromSession, updateUserMetadata, createVenue as createVenueApi, fetchUserVenues as fetchUserVenuesApi } from './supabase-client';
import { Venue } from '../../types';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  venueId: string | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  selectVenue: (venueId: string) => Promise<void>;
  createVenue: (name: string) => Promise<string>;
  fetchUserVenues: () => Promise<Venue[]>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const VENUE_STORAGE_KEY = 'selectedVenueId';

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [venueId, setVenueId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  // Derive venueId from localStorage first, then session, then null
  const deriveVenueId = (session: Session | null): string | null => {
    // First check localStorage
    const storedVenueId = localStorage.getItem(VENUE_STORAGE_KEY);
    if (storedVenueId) {
      return storedVenueId;
    }
    
    // Then check session user_metadata
    if (session) {
      return getVenueIdFromSession(session);
    }
    
    // Finally return null (will trigger venue selection UI)
    return null;
  };

  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setVenueId(deriveVenueId(session));
      setLoading(false);
    });

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      setVenueId(deriveVenueId(session));
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signIn = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) throw error;

    setSession(data.session);
    setUser(data.user);
    setVenueId(deriveVenueId(data.session));
  };

  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;

    // Clear localStorage
    localStorage.removeItem(VENUE_STORAGE_KEY);

    setSession(null);
    setUser(null);
    setVenueId(null);
  };

  const selectVenue = async (venueId: string) => {
    // Store in localStorage for persistence
    localStorage.setItem(VENUE_STORAGE_KEY, venueId);
    
    // Update user_metadata
    await updateUserMetadata(venueId);
    
    // Update state
    setVenueId(venueId);
  };

  const createVenue = async (name: string): Promise<string> => {
    console.log('[AuthContext] createVenue called with name:', name);
    
    try {
      // Create venue in database
      const newVenueId = await createVenueApi(name);
      console.log('[AuthContext] Venue created with ID:', newVenueId);
      
      // Automatically select the new venue
      await selectVenue(newVenueId);
      console.log('[AuthContext] Venue selected successfully');
      
      return newVenueId;
    } catch (error) {
      console.error('[AuthContext] createVenue failed:', error);
      throw error;
    }
  };

  const fetchUserVenues = async (): Promise<Venue[]> => {
    return await fetchUserVenuesApi();
  };

  const value = {
    user,
    session,
    venueId,
    loading,
    signIn,
    signOut,
    selectVenue,
    createVenue,
    fetchUserVenues,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
